const CryptoJS = require("crypto-js");

const getEnvValue = (key) => {
  const value = process.env[key];
  return typeof value === "string" ? value.trim() : "";
};

const readFirstEnv = (keys, fallback = "") => {
  for (const key of keys) {
    const value = getEnvValue(key);
    if (value) {
      return value;
    }
  }
  return fallback;
};

const warnMissing = (label, keys) => {
  const value = readFirstEnv(keys);
  if (!value) {
    console.error(`[cryptoService] Missing env for ${label}. Checked: ${keys.join(", ")}`);
  }
  return value;
};

export const getCryptoConfig = () => {
  return {
    // Existing env names + compatibility aliases from alternate SEC-001 drafts.
    legacyAesKey: warnMissing("legacyAesKey", ["REACT_APP_LEGACY_AES_KEY", "REACT_APP_CRYPTO_3DES_KEY"]),
    websocketAesKey: warnMissing("websocketAesKey", ["REACT_APP_WEBSOCKET_AES_KEY", "REACT_APP_CRYPTO_WS_KEY"]),
    aes256Key: warnMissing("aes256Key", ["REACT_APP_AES256_KEY", "REACT_APP_CRYPTO_AES_KEY"]),
    aes256Iv: warnMissing("aes256Iv", ["REACT_APP_AES256_IV", "REACT_APP_CRYPTO_AES_IV"]),
    tripleDesEncCode: readFirstEnv(["REACT_APP_TRIPLE_DES_ENC_CODE", "REACT_APP_CRYPTO_3DES_ENC"], "QUJDREVGR0g="),
  };
};

const config = getCryptoConfig();

// Function to convert a string to Base64
export function stringToBase64(str) {
  return CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(str));
}

// Function to convert Base64 to a string
export function base64ToString(base64) {
  return CryptoJS.enc.Utf8.stringify(CryptoJS.enc.Base64.parse(base64));
}

export function decryptBase64(base64String) {
  const bytes = CryptoJS.AES.decrypt(base64String, config.legacyAesKey);
  return bytes.toString(CryptoJS.enc.Utf8);
}

export const DecryptedList = (response) => {
  const key = CryptoJS.enc.Utf8.parse(config.legacyAesKey);
  const iv = CryptoJS.enc.Base64.parse(config.tripleDesEncCode);
  const bytes = CryptoJS.TripleDES.decrypt(response, key, {
    mode: CryptoJS.mode.CBC,
    iv: iv,
  });
  return bytes.toString(CryptoJS.enc.Utf8);
};

// Encrypt Data
export const EncryptedList = (text) => {
  const key = CryptoJS.enc.Utf8.parse(config.legacyAesKey);
  const iv = CryptoJS.enc.Base64.parse(config.tripleDesEncCode);
  const encoded = CryptoJS.enc.Utf8.parse(text);
  const bytes = CryptoJS.TripleDES.encrypt(encoded, key, {
    mode: CryptoJS.mode.CBC,
    iv: iv,
  });
  return bytes.toString();
};

// Decrypt ID And name
export const Decrypt_Id_Name = (data, key) => {
  if (data === null || data === undefined || data === "") {
    return "";
  }

  try {
    const decryptedText = CryptoJS.AES.decrypt(data, key).toString(CryptoJS.enc.Utf8);
    if (!decryptedText) {
      return "";
    }
    return JSON.parse(decryptedText);
  } catch (err) {
    console.warn("[cryptoService] Decrypt_Id_Name failed:", err?.message || err);
    return "";
  }
};

export const Decrypt_Id = (data, key) => {
  const bytes = CryptoJS.AES.decrypt(data, key);
  return bytes.toString(CryptoJS.enc.Utf8);
};

export const Encrypted_Id_Name = (data, key) => {
  return CryptoJS.AES.encrypt(JSON.stringify(data), key).toString();
};

export const Encrypted_Password = (data) => {
  return CryptoJS.AES.encrypt(JSON.stringify(data)).toString();
};

// WebSocket data encrypt and decrypt
// Keep existing mode/format for backward compatibility.
export function encrypt(plain) {
  const key = CryptoJS.enc.Utf8.parse(config.websocketAesKey);
  const encryptedData = CryptoJS.AES.encrypt(plain, key, {
    mode: CryptoJS.mode.ECB,
    padding: CryptoJS.pad.Pkcs7
  });
  return CryptoJS.enc.Base64.stringify(CryptoJS.enc.Hex.parse(encryptedData.ciphertext.toString()));
}

export function decrypt(secret) {
  const key = CryptoJS.enc.Utf8.parse(config.websocketAesKey);
  const decryptedData = CryptoJS.AES.decrypt(secret, key, {
    mode: CryptoJS.mode.ECB,
    padding: CryptoJS.pad.Pkcs7
  });
  return decryptedData.toString(CryptoJS.enc.Utf8);
}

const aesEncDecKey = CryptoJS.enc.Utf8.parse(config.aes256Key);
const iv = CryptoJS.enc.Utf8.parse(config.aes256Iv);

export function encryptAndEncodeToBase64(stringToEncrypt) {
  const encrypted = CryptoJS.AES.encrypt(stringToEncrypt, aesEncDecKey, {
    iv: iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });
  const encryptedBase64 = CryptoJS.enc.Base64.stringify(encrypted.ciphertext);
  const encryptedBase64String = CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(encryptedBase64));
  return encryptedBase64String;
}

export const Aes256Base64DecryptString = (base64ToDecrypt) => {
  if (base64ToDecrypt === null || base64ToDecrypt === undefined || base64ToDecrypt === "") {
    return "";
  }
  try {
    // `encryptAndEncodeToBase64` Base64-encodes the ciphertext twice, so
    // unwrap the outer layer first, then build a CipherParams from the inner
    // Base64 ciphertext before handing it to AES.decrypt with the IV.
    let cipherInput;
    try {
      const innerBase64 = CryptoJS.enc.Utf8.stringify(
        CryptoJS.enc.Base64.parse(base64ToDecrypt)
      );
      if (innerBase64 && /^[A-Za-z0-9+/=\r\n]+$/.test(innerBase64)) {
        cipherInput = CryptoJS.lib.CipherParams.create({
          ciphertext: CryptoJS.enc.Base64.parse(innerBase64),
        });
      }
    } catch (_) {
      cipherInput = undefined;
    }
    // Fallback: treat input as a single-Base64 ciphertext (legacy values).
    if (!cipherInput) {
      cipherInput = CryptoJS.lib.CipherParams.create({
        ciphertext: CryptoJS.enc.Base64.parse(base64ToDecrypt),
      });
    }
    const decrypted = CryptoJS.AES.decrypt(cipherInput, aesEncDecKey, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });
    if (!decrypted || !decrypted.sigBytes || decrypted.sigBytes < 0) {
      return "";
    }
    return decrypted.toString(CryptoJS.enc.Utf8);
  } catch (err) {
    console.warn("[cryptoService] Aes256Base64DecryptString failed:", err?.message || err);
    return "";
  }
};

export const Aes256EncryptAndConvertBase64 = (stringToEncrypt) => {
  const encrypted = CryptoJS.AES.encrypt(stringToEncrypt, aesEncDecKey, {
    iv: iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });
  const encryptedBase64 = CryptoJS.enc.Base64.stringify(encrypted.ciphertext);
  const encryptedBase64String = CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(encryptedBase64));
  return encryptedBase64String;
};

export const Aes256Encrypt = async (data) => {
  const encrypted = CryptoJS.AES.encrypt(data, aesEncDecKey, {
    iv: iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });
  return encrypted.toString();
};

export const Aes256Decrypt = async (encryptedData) => {
  const decrypted = CryptoJS.AES.decrypt(encryptedData, aesEncDecKey, {
    iv: iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });
  return decrypted.toString(CryptoJS.enc.Utf8);
};
